// ─── PaywallTiers.jsx — Subscription tier selection ──────────────────────────
// Matches: deep navy bg, violet/teal gradient accents, glass cards, spring anims
import { useState } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";
import { supabase } from "../hooks/useData.js";
import { TIER_CONFIG } from "../lib/billing/tierConfig.js";

// Display copy lives here (it's marketing copy that shouldn't pollute the
// limit definitions in tierConfig.js). Pricing + tier IDs come from
// TIER_CONFIG so a single change updates landing, paywall, server, AND
// the tier sync helper at once.
//
// Copy rules:
//   • Plain English, no jargon (no "SLA", "DPA", "REST API", "white-label").
//   • Each tier lists only the features introduced at its level — the "plus"
//     line at the top makes it obvious you keep everything from the tier below.
//   • No struck-through/off rows — if it's not listed, the next tier has it.
const TIER_DISPLAY = {
  starter: {
    tagline:"Know where you stand",
    gradient:"linear-gradient(135deg, #8b5cf6 0%, #6c5ce7 100%)",
    features:[
      "1 brand",
      "Up to 3 teammates",
      "300 AI messages / month (web chat)",
    ],
  },
  pro: {
    tagline:"Plan the future, not just track it",
    gradient:"linear-gradient(135deg, #06b6d4 0%, #8b5cf6 100%)",
    popular: true,
    inheritsFrom:"Starter",
    features:[
      "3 brands & 10 teammates",
      "AI on WhatsApp + Telegram — 3,000 messages / month",
      "AI notifications — reorder · payment · stockout",
      "CSV + PDF export for any page",
      "PDF import for any order or transaction",
    ],
  },
  business: {
    tagline:"Scale + trust",
    gradient:"linear-gradient(135deg, #10b981 0%, #06b6d4 100%)",
    inheritsFrom:"Pro",
    features:[
      "Unlimited brands",
      "Unlimited teammates",
      "Unlimited AI messages",
      "Your logo on exported PDFs",
      "WhatsApp priority support — 24h best-effort reply",
    ],
  },
};

// Combine canonical limits + display copy into the array the render uses.
const TIERS = ["starter", "pro", "business"].map(id => ({
  id,
  name: TIER_CONFIG[id].label,
  monthlyPrice: TIER_CONFIG[id].monthlyPrice,
  yearlyPrice: TIER_CONFIG[id].yearlyPrice,
  ...TIER_DISPLAY[id],
}));

// Shown once below the cards, not per-tier.
const INCLUDED_IN_EVERY_PLAN = [
  "What-if simulators",
  "Team activity log",
  "14-day free trial, cancel anytime",
];

export default function PaywallTiers({ session, mode }) {
  const T = useTheme();
  const [selected, setSelected] = useState("pro");
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(null);
  const isYearly = billing === "yearly";
  const isTeamPaused = mode === "team-member-paused";

  async function handleCheckout(tier) {
    setLoading(true);
    try {
      const tok = (await supabase.auth.getSession()).data?.session?.access_token;
      const res = await fetch("/api/create-checkout", {
        method:"POST", headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},
        body:JSON.stringify({ tier, billing }),
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  async function handlePortal() {
    setLoading(true);
    try {
      const tok = (await supabase.auth.getSession()).data?.session?.access_token;
      const res = await fetch("/api/billing-portal", {
        method:"POST", headers:{"Content-Type":"application/json",Authorization:`Bearer ${tok}`},
      });
      const { url } = await res.json();
      if (url) window.location.href = url;
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  return (
    <div style={{
      minHeight:"100vh", background:T.bg, fontFamily:FONT,
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
      padding:"40px 20px", position:"relative", overflow:"hidden",
    }}>
      {/* Subtle gradient glow behind cards */}
      <div style={{ position:"absolute", top:"20%", left:"50%", transform:"translateX(-50%)",
        width:"80vw", height:"60vh", borderRadius:"50%",
        background:"radial-gradient(ellipse, rgba(139,92,246,0.12) 0%, rgba(6,182,212,0.06) 40%, transparent 70%)",
        pointerEvents:"none", filter:"blur(60px)",
      }}/>

      {/* Animated logo keyframes — scoped to this page to avoid colliding */}
      <style>{`@keyframes pwtSpin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ textAlign:"center", marginBottom:44, position:"relative", zIndex:1, animation:"sfFadeIn 0.4s ease both" }}>
        {/* Animated CashHorizon logo — same pattern as LoadingScreen (gradient
            square + two counter-rotating rings). No text inside. */}
        <div style={{
          position:"relative", width:64, height:64, margin:"0 auto 18px",
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <div style={{
            width:56, height:56, borderRadius:16,
            background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
            boxShadow:"0 12px 40px rgba(139,92,246,0.5)",
          }}/>
          <div style={{
            position:"absolute", inset:-4, borderRadius:20,
            border:"3px solid transparent",
            borderTopColor:"#8b5cf6", borderRightColor:"#06b6d4",
            animation:"pwtSpin 1.2s linear infinite",
          }}/>
          <div style={{
            position:"absolute", inset:-9, borderRadius:25,
            border:"1.5px solid transparent",
            borderBottomColor:"#8b5cf6", borderLeftColor:"#06b6d4",
            opacity:0.35,
            animation:"pwtSpin 2.4s linear infinite reverse",
          }}/>
        </div>
        <div style={{ fontSize:30, fontWeight:800, color:T.text, letterSpacing:-0.8, marginBottom:8 }}>
          {isTeamPaused ? "Your team's account is paused" : "Choose Your Plan"}
        </div>
        <div style={{ fontSize:15, color:T.textSub, lineHeight:1.6, maxWidth:480, margin:"0 auto" }}>
          {isTeamPaused
            ? "Ask the account owner to resubscribe — your shared data is intact and will be available the moment they're back."
            : "Start with a 14-day free trial. Cancel anytime."}
        </div>

        {/* Reassurance banner — data is safe */}
        <div style={{
          marginTop:18, display:"inline-flex", alignItems:"center", gap:10,
          padding:"10px 16px", borderRadius:RADIUS.md,
          background:"rgba(16,185,129,0.10)", border:"1px solid rgba(16,185,129,0.35)",
          color:T.green, fontSize:13, fontWeight:600, lineHeight:1.5, maxWidth:560,
        }}>
          <span style={{ fontSize:18 }}>🔒</span>
          <span style={{ color:T.text, fontWeight:500 }}>
            <strong style={{ color:T.green }}>Your data is safe.</strong> Every brand, order, target, and decision is preserved. {isTeamPaused ? "It will be there the moment the owner resubscribes." : "Resubscribe any time and pick up exactly where you left off."}
          </span>
        </div>

        {/* Billing toggle (hidden when team-paused — they can't subscribe) */}
        {!isTeamPaused && (
        <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:24, background:T.card, borderRadius:RADIUS.pill, padding:"4px 5px", border:`1px solid ${T.border}` }}>
          <button onClick={()=>setBilling("monthly")} style={{
            padding:"8px 20px", borderRadius:RADIUS.pill, fontSize:13, fontWeight:700, fontFamily:FONT,
            border:"none", cursor:"pointer",
            background: !isYearly ? T.accent : "transparent",
            color: !isYearly ? "#fff" : T.textSub,
            transition:`all 0.2s ${SPRING}`,
          }}>Monthly</button>
          <button onClick={()=>setBilling("yearly")} style={{
            padding:"8px 20px", borderRadius:RADIUS.pill, fontSize:13, fontWeight:700, fontFamily:FONT,
            border:"none", cursor:"pointer", position:"relative",
            background: isYearly ? T.accent : "transparent",
            color: isYearly ? "#fff" : T.textSub,
            transition:`all 0.2s ${SPRING}`,
          }}>
            Annual
            <span style={{
              position:"absolute", top:-10, right:-12, fontSize:9, fontWeight:800,
              background:"linear-gradient(135deg,#10b981,#06b6d4)", color:"#fff",
              padding:"2px 7px", borderRadius:RADIUS.pill, whiteSpace:"nowrap",
            }}>2 months free</span>
          </button>
        </div>
        )}
      </div>

      {/* Team-paused: just a sign-out option */}
      {isTeamPaused && (
        <div style={{ position:"relative", zIndex:1, marginTop:8, display:"flex", flexDirection:"column", alignItems:"center", gap:14 }}>
          <button onClick={() => supabase.auth.signOut()} style={{
            padding:"12px 28px", borderRadius:RADIUS.md, fontSize:14, fontWeight:700, fontFamily:FONT,
            background:"transparent", border:`1px solid ${T.border}`, color:T.textSub, cursor:"pointer",
          }}>Sign out</button>
          <div style={{ fontSize:11, color:T.textMuted }}>You'll regain full access automatically once the owner resubscribes.</div>
        </div>
      )}

      {/* Tier cards */}
      {!isTeamPaused && (
      <div style={{ display:"flex", gap:20, flexWrap:"wrap", justifyContent:"center", maxWidth:1000, width:"100%", position:"relative", zIndex:1 }}>
        {TIERS.map((tier, i) => {
          const isSel = selected === tier.id;
          const isHov = hovered === tier.id;
          return (
            <div key={tier.id}
              onClick={() => setSelected(tier.id)}
              onMouseEnter={() => setHovered(tier.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                flex:"1 1 280px", maxWidth:320, position:"relative",
                background:T.card, borderRadius:RADIUS.xl,
                border:`2px solid ${isSel ? T.accent+"80" : T.border}`,
                padding:"32px 24px 28px",
                cursor:"pointer",
                boxShadow: isSel
                  ? `0 16px 48px rgba(139,92,246,0.25), 0 0 0 1px ${T.accent}40`
                  : isHov ? T.shadowLg : T.shadow,
                transition:`all 0.28s ${SPRING}`,
                transform: isSel ? "scale(1.02)" : isHov ? "translateY(-4px)" : "none",
                animation:`sfFadeIn 0.4s ${0.1 + i*0.08}s ease both`,
              }}>
              {/* Popular badge */}
              {tier.popular && (
                <div style={{
                  position:"absolute", top:-13, left:"50%", transform:"translateX(-50%)",
                  background:"linear-gradient(135deg,#8b5cf6,#06b6d4)",
                  color:"#fff", fontSize:9, fontWeight:800, letterSpacing:1.2,
                  textTransform:"uppercase", padding:"5px 16px",
                  borderRadius:RADIUS.pill,
                  boxShadow:"0 4px 16px rgba(139,92,246,0.4)",
                }}>Most Popular</div>
              )}

              {/* Accent bar */}
              <div style={{
                height:4, borderRadius:2, marginBottom:20,
                background:tier.gradient, opacity: isSel ? 1 : 0.4,
                transition:"opacity 0.2s",
              }}/>

              <div style={{ fontSize:22, fontWeight:800, color:T.text, letterSpacing:-0.5, marginBottom:4 }}>{tier.name}</div>
              <div style={{ fontSize:13, color:T.textSub, marginBottom:24, lineHeight:1.5 }}>{tier.tagline}</div>

              {/* Price */}
              <div style={{ display:"flex", alignItems:"baseline", gap:3, marginBottom:24 }}>
                <span style={{ fontSize:40, fontWeight:800, color:T.text, letterSpacing:-1 }}>€{isYearly ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice}</span>
                <span style={{ fontSize:14, color:T.textSub, fontWeight:500 }}>/mo</span>
              </div>
              {isYearly && (
                <div style={{ fontSize:12, color:T.green, fontWeight:600, marginTop:-18, marginBottom:18 }}>
                  €{tier.yearlyPrice}/yr — save €{tier.monthlyPrice * 12 - tier.yearlyPrice}/yr
                </div>
              )}

              {/* "Everything in X, plus:" — only on Pro & Business */}
              {tier.inheritsFrom && (
                <div style={{
                  fontSize:12, color:T.textSub, fontWeight:600,
                  padding:"0 0 10px", marginBottom:8,
                  borderBottom:`1px dashed ${T.border}`,
                }}>
                  Everything in <strong style={{ color:T.text }}>{tier.inheritsFrom}</strong>, plus:
                </div>
              )}

              {/* Features — plain list, no off-state rows */}
              <div style={{ marginBottom:24 }}>
                {tier.features.map((text, j) => (
                  <div key={j} style={{
                    display:"flex", alignItems:"flex-start", gap:9, padding:"6px 0",
                    fontSize:13, color:T.text, lineHeight:1.45,
                  }}>
                    <span style={{
                      width:18, height:18, borderRadius:6,
                      background: T.green+"18",
                      border:`1.5px solid ${T.green}`,
                      display:"flex", alignItems:"center", justifyContent:"center",
                      fontSize:10, fontWeight:900, color:T.green,
                      flexShrink:0, marginTop:1,
                    }}>✓</span>
                    <span>{text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button onClick={(e) => { e.stopPropagation(); handleCheckout(tier.id); }} disabled={loading} style={{
                width:"100%", padding:"13px",
                background: isSel ? tier.gradient : "transparent",
                color: isSel ? "#fff" : T.accent,
                border:`2px solid ${isSel ? "transparent" : T.accent+"40"}`,
                borderRadius:RADIUS.md,
                fontSize:15, fontWeight:700, fontFamily:FONT,
                cursor:loading?"wait":"pointer",
                boxShadow: isSel ? "0 8px 24px rgba(139,92,246,0.3)" : "none",
                transition:`all 0.2s ${SPRING}`,
              }}>
                {loading ? "Please wait…" : "Start Free Trial"}
              </button>
            </div>
          );
        })}
      </div>
      )}

      {/* Included in every plan — single strip below all tiers */}
      {!isTeamPaused && (
        <div style={{
          maxWidth: 900, margin: "28px auto 0", padding: "14px 22px",
          display: "inline-flex", flexWrap: "wrap", alignItems: "center",
          justifyContent: "center", gap: "8px 14px",
          borderRadius: RADIUS.lg,
          background: "rgba(139,92,246,0.06)",
          border: `1px dashed ${T.accent}40`,
          fontSize: 13, color: T.textSub, fontFamily: FONT, lineHeight: 1.5,
          position: "relative", zIndex: 1,
        }}>
          <span style={{
            color: T.text, fontWeight: 700, fontSize: 11,
            textTransform: "uppercase", letterSpacing: 1, marginRight: 4,
          }}>Included in every plan</span>
          {INCLUDED_IN_EVERY_PLAN.map((item, i) => (
            <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
              {i > 0 && <span style={{ color: T.border }}>·</span>}
              <span>{item}</span>
            </span>
          ))}
        </div>
      )}
      {!isTeamPaused && (
        <div style={{
          marginTop: 12, fontSize: 12, color: T.textMuted, textAlign: "center",
          position: "relative", zIndex: 1,
        }}>
          <span style={{ color: "#fb923c" }}>⏳</span> Coming Q4 2026 on Business — Developer API
        </div>
      )}

      {/* Footer actions — only when offering plans */}
      {!isTeamPaused && (
      <div style={{ marginTop:32, display:"flex", gap:20, alignItems:"center", position:"relative", zIndex:1 }}>
        <button onClick={handlePortal} disabled={loading} style={{
          background:"transparent", border:`1px solid ${T.border}`,
          borderRadius:RADIUS.sm, color:T.textSub, fontSize:13,
          padding:"9px 18px", fontFamily:FONT, cursor:"pointer",
        }}>Manage existing billing</button>
        <button onClick={()=>supabase.auth.signOut()} style={{
          background:"none", border:"none", color:T.textSub,
          fontSize:12, fontFamily:FONT, cursor:"pointer",
        }}>Sign out</button>
      </div>
      )}
    </div>
  );
}
